import React, { Component } from "react";
import { Input, Menu } from "antd";
import { SearchOutlined } from "@ant-design/icons";

const { SubMenu } = Menu;

// Normaliza texto para comparar sin distinguir mayúsculas ni acentos.
const normalizarTexto = (str) =>
  String(str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

// Extrae recursivamente el texto visible de un nodo de React
// (soporta strings sueltos, <NavLink>Texto</NavLink> y <span>Texto</span>).
const extraerTextoNodo = (nodo) => {
  if (nodo == null || typeof nodo === "boolean") return "";
  if (typeof nodo === "string" || typeof nodo === "number") return String(nodo);
  if (Array.isArray(nodo)) return nodo.map(extraerTextoNodo).join(" ");
  if (React.isValidElement(nodo)) return extraerTextoNodo(nodo.props.children);
  return "";
};

// Recorre el árbol de Menu.Item/SubMenu y devuelve una lista PLANA de los
// Menu.Item (hojas) cuyo nombre coincide con el término, o cuyo grupo
// (SubMenu) padre coincide. No muta el árbol original; solo lo lee.
const aplanarMenuItems = (nodos, termino, ancestroCoincide = false) => {
  let resultado = [];
  React.Children.forEach(nodos, (nodo) => {
    if (!React.isValidElement(nodo)) return;

    if (nodo.type === React.Fragment) {
      resultado = resultado.concat(
        aplanarMenuItems(nodo.props.children, termino, ancestroCoincide)
      );
      return;
    }

    if (nodo.type === SubMenu) {
      const tituloCoincide = normalizarTexto(nodo.props.title).includes(termino);
      resultado = resultado.concat(
        aplanarMenuItems(nodo.props.children, termino, ancestroCoincide || tituloCoincide)
      );
      return;
    }

    if (nodo.type === Menu.Item) {
      const etiqueta = normalizarTexto(extraerTextoNodo(nodo.props.children));
      if (ancestroCoincide || etiqueta.includes(termino)) {
        resultado.push(nodo);
      }
    }
  });
  return resultado;
};

/**
 * Buscador del menú lateral.
 *
 * Encapsula el input de búsqueda y la lógica de filtrado. Maneja su propio
 * estado de texto; Panel solo lo instancia. Usa render-prop: entrega, vía
 * `children`, los items a pintar dentro del <Menu> (el árbol completo si no
 * hay búsqueda, o la lista plana de coincidencias si se está buscando).
 *
 * Props:
 *  - menuItems: fragmento (<>...</>) con todos los Menu.Item/SubMenu.
 *  - habilitado: si es false, no muestra input y entrega el árbol intacto.
 *  - children: función (items) => JSX que renderiza el <Menu> con esos items.
 *  - oscuro: si es true, estiliza el input para fondo oscuro (Sider dark).
 */
class BuscadorMenuInput extends Component {
  state = { search: "" };

  render() {
    const { menuItems, habilitado, children, placeholder, oscuro } = this.props;

    // Deshabilitado (o menú colapsado): sin input y con el árbol original.
    if (!habilitado) {
      return children(menuItems);
    }

    const termino = normalizarTexto(this.state.search.trim());
    const items = termino
      ? aplanarMenuItems(menuItems.props.children, termino)
      : menuItems;

    return (
      <>
        <div
          className={oscuro ? "buscador-menu-wrapper-oscuro" : "buscador-menu-wrapper"}
          style={{
            padding: "10px 12px",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Input
            allowClear
            className={oscuro ? "buscador-menu-input-oscuro" : undefined}
            style={{ borderRadius: 8 }}
            prefix={
              <SearchOutlined
                style={{ color: oscuro ? "rgba(255,255,255,0.45)" : "#bfbfbf" }}
              />
            }
            placeholder={placeholder || "Buscar módulo..."}
            value={this.state.search}
            onChange={(e) => this.setState({ search: e.target.value })}
          />
        </div>
        {children(items)}
      </>
    );
  }
}

export default BuscadorMenuInput;
